// ReadingScreen - Display tarot reading results with animations
import React from 'react';
import { motion } from 'framer-motion';
import { CheckSquare, ArrowRight } from 'lucide-react';
import { Button } from '../common/Button';
import { PageContainer, GlassCard } from '../layout/PageContainer';
import { CardSpread } from '../tarot/TarotCard';
import { MarkdownText } from '../common/MarkdownText';

export const ReadingScreen = ({
    question,
    cards,
    interpretation,
    onDone,
    onCardClick,
    onGetActionableSteps
}) => {
    const hasCards = cards && cards.length > 0;
    const hasInterpretation = !!interpretation;

    return (
        <PageContainer className="py-6">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="max-w-3xl mx-auto"
            >
                {/* Header */}
                <div className="text-center mb-8">
                    <motion.h2
                        className="text-2xl sm:text-3xl md:text-4xl font-serif text-theme mb-3"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        Your Cosmic Guidance
                    </motion.h2>
                    <motion.p
                        className="text-theme-muted text-sm sm:text-base italic max-w-md mx-auto"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                    >
                        "{question}"
                    </motion.p>
                </div>

                {/* Cards Section */}
                <motion.div
                    className="mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                >
                    <h3 className="text-lg font-serif text-theme-secondary mb-4 text-center">
                        The Cards Revealed
                    </h3>
                    <CardSpread
                        cards={cards}
                        onCardClick={onCardClick}
                        isLoading={!hasCards}
                    />
                    {hasCards && (
                        <p className="text-center text-theme-muted text-xs mt-3 italic">
                            Tap any card to unlock its deeper wisdom
                        </p>
                    )}
                </motion.div>

                {/* Interpretation */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                >
                    {hasInterpretation ? (
                        <GlassCard className="border-theme-accent">
                            <h3 className="text-xl font-serif text-theme-secondary mb-4 flex items-center gap-2">
                                <span className="text-2xl">✨</span>
                                The Cosmos Whispers
                            </h3>
                            <MarkdownText size="lg">
                                {interpretation}
                            </MarkdownText>

                            {/* Action button */}
                            <motion.div
                                className="mt-6 flex justify-center"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1.2 }}
                            >
                                <Button
                                    variant="secondary"
                                    size="md"
                                    onClick={onGetActionableSteps}
                                    icon={CheckSquare}
                                    className="border-teal-500/30 hover:border-teal-500/50"
                                >
                                    Suggest Actionable Steps
                                </Button>
                            </motion.div>
                        </GlassCard>
                    ) : (
                        <GlassCard className="text-center py-12">
                            <motion.div
                                animate={{
                                    scale: [1, 1.1, 1],
                                    opacity: [0.5, 1, 0.5]
                                }}
                                transition={{ duration: 2, repeat: Infinity }}
                            >
                                <span className="text-4xl">⭐</span>
                            </motion.div>
                            <p className="text-theme-secondary text-lg mt-4">
                                Receiving wisdom from the universe...
                            </p>
                        </GlassCard>
                    )}
                </motion.div>

                {/* Done Button */}
                <motion.div
                    className="mt-8 flex justify-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1 }}
                >
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={onDone}
                        icon={ArrowRight}
                        iconPosition="right"
                    >
                        Ask Another Question
                    </Button>
                </motion.div>
            </motion.div>
        </PageContainer>
    );
};

export default ReadingScreen;
